import {useMemo, useState} from 'react';

import {useTranslation} from 'react-i18next';

import {Autocomplete, Box, TextField} from '@mui/material';

import {
  ALIGNMENT_SHAPE_MAX_ROLE_OVERLAYS,
  hasAlignmentShapeScores,
} from './alignmentShapeChartConfig';
import {WorkforceDashboardAlignmentShapeRadar} from './WorkforceDashboardAlignmentShapeRadar';
import {WorkforceDashboardSectionCard} from './WorkforceDashboardSectionCard';
import {EmptyStateCard} from '../../../../../components/EmptyStateCard/EmptyStateCard';
import type {WorkforceDashboardAlignmentShape} from '../../types/workforceDashboardTypes';

export interface Props {
  data: WorkforceDashboardAlignmentShape | null;
}

export const WorkforceDashboardAlignmentShapeChart = ({data}: Props) => {
  const {t} = useTranslation('translation', {keyPrefix: 'workforceDashboardPage.sections.alignmentShape'});
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);

  const roleOptions = useMemo(() => (data?.roles ?? []).map((role) => role.targetProfileTitle), [data?.roles]);

  const dimensionLabels = useMemo(
    () => ({
      coreCompetencies: t('dimensions.coreCompetencies'),
      hardSkills: t('dimensions.hardSkills'),
      tools: t('dimensions.tools'),
      domainKnowledge: t('dimensions.domainKnowledge'),
      softSkills: t('dimensions.softSkills'),
      responsibilities: t('dimensions.responsibilities'),
    }),
    [t]
  );

  const chartDatasets = useMemo(() => {
    if (!data || !hasAlignmentShapeScores(data.companyAverage)) {
      return [];
    }

    const datasets = [
      {
        label: t('companyLabel'),
        scores: data.companyAverage,
      },
    ];

    selectedRoles.forEach((roleTitle) => {
      const role = data.roles.find((item) => item.targetProfileTitle === roleTitle);
      if (role && hasAlignmentShapeScores(role.scores)) {
        datasets.push({
          label: role.targetProfileTitle,
          scores: role.scores,
        });
      }
    });

    return datasets;
  }, [data, selectedRoles, t]);

  const handleRoleChange = (_event: unknown, value: string[]) => {
    setSelectedRoles(value.slice(0, ALIGNMENT_SHAPE_MAX_ROLE_OVERLAYS));
  };

  if (!data || !hasAlignmentShapeScores(data.companyAverage)) {
    return (
      <WorkforceDashboardSectionCard title={t('title')}>
        <EmptyStateCard />
      </WorkforceDashboardSectionCard>
    );
  }

  return (
    <WorkforceDashboardSectionCard title={t('title')} description={t('description') as string}>
      <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, width: '100%'}}>
        <Autocomplete
          multiple
          options={roleOptions}
          value={selectedRoles}
          onChange={handleRoleChange}
          disableCloseOnSelect
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('roleSelector.label') as string}
              placeholder={t('roleSelector.placeholder') as string}
              helperText={t('roleSelector.helper') as string}
            />
          )}
          sx={{width: '100%'}}
        />

        <WorkforceDashboardAlignmentShapeRadar dimensionLabels={dimensionLabels} datasets={chartDatasets} />
      </Box>
    </WorkforceDashboardSectionCard>
  );
};
